/**
 * Cliente HTTP + tipos da API Storeo — consumido por apps/painel e apps/storefront.
 *
 * Os TIPOS de request/response vêm de `./generated/schema.ts`, gerado do OpenAPI que a API
 * publica. NUNCA redigite um tipo de contrato aqui (guardrail — ADR 0002 §12 / checklist de PR).
 * Regenerar: `pnpm gen:api-client` (a API precisa estar no ar).
 */
import type { components, paths } from './generated/schema';

export type ApiPaths = paths;

export interface StoreoClientOptions {
  /** ex.: `http://localhost:8080` (dev) ou `https://storeo.rgtech.app.br` */
  baseUrl: string;
  /** token do parceiro (painel) ou da sessão do comprador (storefront) */
  getToken?: () => string | null | undefined;
  fetch?: typeof fetch;
}

export class StoreoApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: string,
  ) {
    super(`Storeo API ${status}: ${body}`);
    this.name = 'StoreoApiError';
  }
}

export function createStoreoClient(opts: StoreoClientOptions) {
  const base = opts.baseUrl.replace(/\/+$/, '');
  const doFetch = opts.fetch ?? fetch;

  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = opts.getToken?.();
    const res = await doFetch(`${base}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
    if (!res.ok) throw new StoreoApiError(res.status, await res.text());
    return (res.status === 204 ? undefined : await res.json()) as T;
  }

  /** Upload multipart — sem `content-type` fixo (o browser gera o boundary sozinho). */
  async function requestForm<T>(path: string, form: FormData, init?: RequestInit): Promise<T> {
    const token = opts.getToken?.();
    const res = await doFetch(`${base}${path}`, {
      method: 'POST',
      ...init,
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
      body: form,
    });
    if (!res.ok) throw new StoreoApiError(res.status, await res.text());
    return (res.status === 204 ? undefined : await res.json()) as T;
  }

  return {
    request,
    requestForm,
    /** actuator/health — útil pra smoke test do wiring front ↔ API */
    health: () => request<{ status: string }>('/actuator/health'),
  };
}

export type StoreoClient = ReturnType<typeof createStoreoClient>;

/**
 * springdoc doesn't emit `required` for response records, so every field on the generated
 * `StoreView` / `ProductView` is optional. The storefront contract guarantees they're present
 * (`tagline` is the only real nullable). We keep the field NAMES tied to the generated schema
 * (a rename in the API still propagates) and only fix the optionality here.
 * TODO(openapi): drop `Filled` once the API annotates its response schemas as required.
 */
type Filled<T> = { -readonly [K in keyof T]-?: NonNullable<T[K]> };

export type StorefrontSection = Filled<components['schemas']['StorefrontSectionView']>;
export type StorefrontStore = Omit<
  Filled<components['schemas']['StoreView']>,
  'tagline' | 'logoUrl' | 'testimonialQuote' | 'testimonialAuthor' | 'sections'
> & {
  tagline: string | null;
  logoUrl: string | null;
  testimonialQuote: string | null;
  testimonialAuthor: string | null;
  sections: StorefrontSection[];
};
export type StorefrontCatalogItem = Filled<components['schemas']['ProductView']>;

export interface StorefrontHome {
  store: StorefrontStore;
  products: StorefrontCatalogItem[];
}
export interface StorefrontProduct {
  store: StorefrontStore;
  product: StorefrontCatalogItem;
}

/** Storefront reads are public — no token. */
export function createStorefrontClient(opts: Pick<StoreoClientOptions, 'baseUrl' | 'fetch'>) {
  const client = createStoreoClient(opts);
  return {
    home: (subdomain: string, init?: RequestInit) =>
      client.request<StorefrontHome>(`/api/storefront/${encodeURIComponent(subdomain)}`, init),
    product: (subdomain: string, slug: string, init?: RequestInit) =>
      client.request<StorefrontProduct>(
        `/api/storefront/${encodeURIComponent(subdomain)}/products/${encodeURIComponent(slug)}`,
        init,
      ),
  };
}

// ---- Checkout (M3) ---------------------------------------------------------
// Checkout writes/reads are public too — the storefront's own route handlers proxy the browser's
// calls (same-origin, no CORS). Request bodies now carry `required` (springdoc reads the bean
// validation), so `PlaceOrderInput` comes straight from the schema. The response records still
// have no `required` → same `Filled<T>` fix as the storefront types; `pix` is nullable on the
// status read once the order is paid/expired, so that one is patched back to `| null` by hand.

export type PlaceOrderInput = components['schemas']['PlaceOrderRequest'];

export type CheckoutPix = Filled<components['schemas']['Pix']>;

export type PlaceOrderResult = Omit<Filled<components['schemas']['PlaceOrderResponse']>, 'pix'> & {
  pix: CheckoutPix;
};

export type OrderStatus = Omit<
  Filled<components['schemas']['OrderStatusResponse']>,
  'pix' | 'store' | 'item'
> & {
  store: Filled<components['schemas']['StoreBrand']>;
  item: Filled<components['schemas']['Item']>;
  pix: CheckoutPix | null;
};

/** Checkout is public — no token. The subdomain goes in the path (ADR 0007). */
export function createCheckoutClient(opts: Pick<StoreoClientOptions, 'baseUrl' | 'fetch'>) {
  const client = createStoreoClient(opts);
  return {
    placeOrder: (subdomain: string, input: PlaceOrderInput, idempotencyKey?: string) =>
      client.request<PlaceOrderResult>(`/api/checkout/${encodeURIComponent(subdomain)}/orders`, {
        method: 'POST',
        body: JSON.stringify(input),
        headers: idempotencyKey ? { 'idempotency-key': idempotencyKey } : {},
      }),
    orderStatus: (subdomain: string, orderId: string, init?: RequestInit) =>
      client.request<OrderStatus>(
        `/api/checkout/${encodeURIComponent(subdomain)}/orders/${encodeURIComponent(orderId)}`,
        { cache: 'no-store', ...init },
      ),
  };
}

// ---- Delivery (M4) ------------------------------------------------------------
// The storefront S6 shows the download / access lines for a paid order. springdoc emits no
// `required`, so `Filled<T>` again; `url` and `instructions` are genuinely nullable (one xor the
// other per line), so those stay optional after the fix.

export type DeliveryLine = Omit<Filled<components['schemas']['Line']>, 'url' | 'instructions'> & {
  kind: 'file' | 'link';
  url: string | null;
  instructions: string | null;
};

export interface OrderDelivery {
  lines: DeliveryLine[];
}

/** M9 — physical order fulfilment status: `to_ship`, `shipped`, or `null` (not a physical order). */
export type FulfilmentStatus = 'to_ship' | 'shipped' | null;

/** Public — the storefront's own route handler proxies the browser call (same-origin, no token). */
export function createDeliveryClient(opts: Pick<StoreoClientOptions, 'baseUrl' | 'fetch'>) {
  const client = createStoreoClient(opts);
  return {
    orderDelivery: (subdomain: string, orderId: string, init?: RequestInit) =>
      client.request<OrderDelivery>(
        `/api/delivery/${encodeURIComponent(subdomain)}/orders/${encodeURIComponent(orderId)}`,
        { cache: 'no-store', ...init },
      ),
    orderFulfilment: (subdomain: string, orderId: string, init?: RequestInit) =>
      client
        .request<{ status: FulfilmentStatus }>(
          `/api/delivery/${encodeURIComponent(subdomain)}/orders/${encodeURIComponent(orderId)}/fulfilment`,
          { cache: 'no-store', ...init },
        )
        .then((r) => r.status),
  };
}

// ---- Painel do parceiro (M10, ADR 0019) ------------------------------------------------
// Autenticado — token do realm `storeo` (ver apps/painel/src/lib/api.ts). Cresce por camada
// (L1 fundação só precisa de `store`; L2+ acrescenta produtos/vendas/dinheiro/etc.).

export type PanelSection = Filled<components['schemas']['SectionView']>;
export type PanelStore = Omit<
  Filled<components['schemas']['StoreResponse']>,
  'tagline' | 'logoUrl' | 'sections' | 'testimonialQuote' | 'testimonialAuthor'
> & {
  tagline: string | null;
  logoUrl: string | null;
  sections: PanelSection[];
  testimonialQuote: string | null;
  testimonialAuthor: string | null;
};

// -- Produtos (L2) ------------------------------------------------------------------------

export type PanelProduct = Filled<Omit<components['schemas']['ProductListItem'], 'description'>> & {
  description: string | null;
};
export type CreateProductInput = components['schemas']['CreateProductRequest'];
export type UpdateProductInput = components['schemas']['UpdateProductRequest'];
export type PanelVideo = Filled<components['schemas']['VideoResponse']>;
export type PanelGroupLink = Filled<components['schemas']['GroupLinkResponse']>;

// -- Vendas (L3) --------------------------------------------------------------------------

export type PanelOrder = Filled<components['schemas']['OrderResponse']>;
export interface PanelOrdersPage {
  items: PanelOrder[];
  hasMore: boolean;
}
export type PanelSalesSummary = Filled<components['schemas']['Tiles']>;

// -- Dinheiro + Início (L4) -----------------------------------------------------------------

export type PanelLedgerEntry = Filled<components['schemas']['LedgerEntryResponse']>;
export interface PanelLedgerPage {
  items: PanelLedgerEntry[];
  hasMore: boolean;
}
export type PanelChargebackRate = Filled<components['schemas']['View']>;

// -- Assinantes + Campanhas (L5) ------------------------------------------------------------

export type PanelSubscription = Filled<components['schemas']['SubscriptionResponse']>;
export type PanelCampaign = Omit<Filled<components['schemas']['CampaignResponse']>, 'link' | 'sentAt'> & {
  link: string | null;
  sentAt: string | null;
};
export type CreateCampaignInput = components['schemas']['CreateRequest'];

export type UpdateBrandingInput = components['schemas']['BrandingRequest'];
export type UpdateCustomizationInput = components['schemas']['CustomizationRequest'];

// -- Ajustes / repasse (L6) -----------------------------------------------------------------

export type PanelKyc = Filled<components['schemas']['KycResponse']>;
export type UpdatePixKeyInput = components['schemas']['UpdatePixKeyRequest'];

export function createPanelClient(opts: StoreoClientOptions) {
  const client = createStoreoClient(opts);
  return {
    ...client,
    store: (init?: RequestInit) => client.request<PanelStore>('/api/store', { cache: 'no-store', ...init }),
    publishStore: () => client.request<void>('/api/store/publish', { method: 'PATCH' }),
    unpublishStore: () => client.request<void>('/api/store/unpublish', { method: 'PATCH' }),
    updateBranding: (input: UpdateBrandingInput) =>
      client.request<void>('/api/store/branding', { method: 'PATCH', body: JSON.stringify(input) }),
    updateCustomization: (input: UpdateCustomizationInput) =>
      client.request<void>('/api/store/customization', { method: 'PATCH', body: JSON.stringify(input) }),
    uploadLogo: (file: File) => {
      const form = new FormData();
      form.set('file', file);
      return client.requestForm<Filled<components['schemas']['LogoResponse']>>('/api/store/logo', form);
    },

    kyc: (init?: RequestInit) => client.request<PanelKyc>('/api/kyc', { cache: 'no-store', ...init }),
    updatePixKey: (input: UpdatePixKeyInput) =>
      client.request<void>('/api/kyc/pix-key', { method: 'PATCH', body: JSON.stringify(input) }),

    products: (init?: RequestInit) =>
      client.request<PanelProduct[]>('/api/products', { cache: 'no-store', ...init }),
    product: (id: string, init?: RequestInit) =>
      client.request<PanelProduct>(`/api/products/${encodeURIComponent(id)}`, { cache: 'no-store', ...init }),
    createProduct: (input: CreateProductInput) =>
      client.request<Filled<components['schemas']['ProductIdResponse']>>('/api/products', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateProduct: (id: string, input: UpdateProductInput) =>
      client.request<void>(`/api/products/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    publishProduct: (id: string) =>
      client.request<void>(`/api/products/${encodeURIComponent(id)}/publish`, { method: 'PATCH' }),
    unpublishProduct: (id: string) =>
      client.request<void>(`/api/products/${encodeURIComponent(id)}/unpublish`, { method: 'PATCH' }),

    // entrega — um método por canal (ADR 0019 §5 L2)
    uploadDeliveryFile: (productId: string, file: File) => {
      const form = new FormData();
      form.set('file', file);
      return client.requestForm<Filled<components['schemas']['FileIdResponse']>>(
        `/api/products/${encodeURIComponent(productId)}/delivery/file`,
        form,
      );
    },
    setDeliveryLink: (productId: string, linkOrInstructions: string) =>
      client.request<void>(`/api/products/${encodeURIComponent(productId)}/delivery`, {
        method: 'PATCH',
        body: JSON.stringify({ linkOrInstructions }),
      }),
    uploadVideo: (productId: string, file: File, title?: string) => {
      const form = new FormData();
      form.set('file', file);
      if (title) form.set('title', title);
      return client.requestForm<Filled<components['schemas']['VideoIdResponse']>>(
        `/api/products/${encodeURIComponent(productId)}/delivery/video`,
        form,
      );
    },
    videos: (productId: string, init?: RequestInit) =>
      client.request<PanelVideo[]>(`/api/products/${encodeURIComponent(productId)}/delivery/video`, {
        cache: 'no-store',
        ...init,
      }),
    linkTelegramGroup: (productId: string, chatId: string, title: string) =>
      client.request<PanelGroupLink>(`/api/products/${encodeURIComponent(productId)}/delivery/telegram`, {
        method: 'PUT',
        body: JSON.stringify({ chatId, title }),
      }),
    telegramGroup: (productId: string, init?: RequestInit) =>
      client
        .request<PanelGroupLink | undefined>(
          `/api/products/${encodeURIComponent(productId)}/delivery/telegram`,
          { cache: 'no-store', ...init },
        )
        .then((r) => r ?? null),

    orders: (opts: { page?: number; size?: number } = {}) => {
      const q = new URLSearchParams();
      if (opts.page !== undefined) q.set('page', String(opts.page));
      if (opts.size !== undefined) q.set('size', String(opts.size));
      const qs = q.toString();
      return client.request<PanelOrdersPage>(`/api/orders${qs ? `?${qs}` : ''}`, { cache: 'no-store' });
    },
    refundOrder: (orderId: string, reason?: 'refund' | 'chargeback') =>
      client.request<void>('/api/payments/refunds', {
        method: 'POST',
        body: JSON.stringify({ orderId, reason }),
      }),
    markShipped: (orderId: string) =>
      client.request<void>(`/api/delivery/fulfilments/${encodeURIComponent(orderId)}/shipped`, {
        method: 'PATCH',
      }),

    salesSummary: () => client.request<PanelSalesSummary>('/api/orders/summary', { cache: 'no-store' }),
    balance: () => client.request<{ available: number }>('/api/payments/balance', { cache: 'no-store' }),
    ledger: (opts: { page?: number; size?: number } = {}) => {
      const q = new URLSearchParams();
      if (opts.page !== undefined) q.set('page', String(opts.page));
      if (opts.size !== undefined) q.set('size', String(opts.size));
      const qs = q.toString();
      return client.request<PanelLedgerPage>(`/api/payments/ledger${qs ? `?${qs}` : ''}`, { cache: 'no-store' });
    },
    chargebackRate: () =>
      client.request<PanelChargebackRate>('/api/payments/chargeback-rate', { cache: 'no-store' }),
    pendingShipmentCount: () =>
      client
        .request<{ count: number }>('/api/delivery/fulfilments/pending-count', { cache: 'no-store' })
        .then((r) => r.count),

    subscriptions: () =>
      client.request<PanelSubscription[]>('/api/subscriptions', { cache: 'no-store' }),
    cancelSubscription: (id: string, reason?: string) =>
      client.request<void>(`/api/subscriptions/${encodeURIComponent(id)}/cancel`, {
        method: 'PATCH',
        body: JSON.stringify({ reason }),
      }),

    campaigns: () => client.request<PanelCampaign[]>('/api/campaigns', { cache: 'no-store' }),
    createCampaign: (input: CreateCampaignInput) =>
      client.request<PanelCampaign>('/api/campaigns', { method: 'POST', body: JSON.stringify(input) }),
  };
}

/** Painel interno (apps/admin) — `platform-staff`, visão agregada de toda a plataforma. */
export type AdminOverview = Filled<components['schemas']['Overview']>;
export type AdminPartnerSummary = Filled<components['schemas']['PartnerSummary']>;
export type AdminPartnerDetail = Filled<components['schemas']['PartnerDetail']>;
export type AdminHealthReport = Filled<components['schemas']['HealthReport']>;

export function createAdminClient(opts: StoreoClientOptions) {
  const client = createStoreoClient(opts);
  return {
    ...client,
    overview: () => client.request<AdminOverview>('/api/admin/overview', { cache: 'no-store' }),
    partners: (opts: { search?: string; page?: number; size?: number } = {}) => {
      const q = new URLSearchParams();
      if (opts.search) q.set('search', opts.search);
      if (opts.page !== undefined) q.set('page', String(opts.page));
      if (opts.size !== undefined) q.set('size', String(opts.size));
      const qs = q.toString();
      return client.request<AdminPartnerSummary[]>(`/api/admin/partners${qs ? `?${qs}` : ''}`, {
        cache: 'no-store',
      });
    },
    partner: (id: string) =>
      client.request<AdminPartnerDetail>(`/api/admin/partners/${encodeURIComponent(id)}`, {
        cache: 'no-store',
      }),
    health: () => client.request<AdminHealthReport>('/api/admin/health', { cache: 'no-store' }),
  };
}
